import { apiBase, isV2, terminalId } from "./config";
import { createGateway } from "./v2gateway";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

const TOKEN_KEY = "astro.token";
const BRANCH_KEY = "astro.branch";
let branch: string | null = null;
let token: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function loadToken(): string | null {
  try {
    token = localStorage.getItem(TOKEN_KEY);
  } catch {
    token = null;
  }
  return token;
}
export function setToken(t: string | null): void {
  token = t;
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable: the token stays in memory */
  }
}
export function loadBranch(): string | null {
  try {
    branch = localStorage.getItem(BRANCH_KEY);
  } catch {
    branch = null;
  }
  return branch;
}
/** v2: the branch every request works in (sent as X-Branch-Id; the server re-checks it against the token). */
export function setBranch(id: string | null): void {
  branch = id;
  try {
    if (id) localStorage.setItem(BRANCH_KEY, id);
    else localStorage.removeItem(BRANCH_KEY);
  } catch {
    /* storage unavailable */
  }
}
export function getBranch(): string | null {
  return branch;
}
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

async function rawRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${apiBase()}${path}`, {
      method,
      headers: {
        "content-type": "application/json",
        "x-terminal-id": terminalId(),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(branch ? { "x-branch-id": branch } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Cannot reach the server");
  }
  const json = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string; details?: unknown } } & Record<string, unknown>;
  if (!res.ok) {
    if (res.status === 401 && token && path !== "/auth/login") onUnauthorized?.();
    throw new ApiError(res.status, json.error?.code ?? "HTTP_ERROR", json.error?.message ?? res.statusText, json.error?.details);
  }
  return json as T;
}

const gateway = createGateway((method, path, body) => rawRequest(method, path, body));

/** v2 mode: the familiar screens keep their paths; the gateway turns them into native v2 calls and reshapes the answers. */
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (isV2()) {
    const r = await gateway.handle(method, path, body);
    if (r.handled) return r.value as T;
  }
  return rawRequest<T>(method, path, body);
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body ?? {}),
  delete: <T>(path: string) => request<T>("DELETE", path),
};
